import React, { useEffect, useState } from "react";

import { faAngleDown, faCaretDown } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Icon, Spinner, Text } from "@performyard/py-components";
import { Avatar } from "@performyard/py-components";

import ExpandableText from "./ExpandableText";
import FloatingDropdown from "./FloatingDropdown";

import type { NoteType } from "../types";

import { useMeetingNotes } from "../../../../api/hooks";
import { usePastMeetingNotesData } from "../hooks";

import { formatPastMeetingDateTime } from "../utils";

import { config } from "../config";

type PastMeetingItemProps = {
	meetingId: string;
	meetingName: string;
	startDateTime: Date;
	endDateTime: Date;
	participants: Array<{ userId: string; name?: string }>;
	workspaceParticipants?: Array<{ userId: string; name?: string }>;
	isExpanded: boolean;
	onToggle: () => void;
	userId: string;
	isInteractionDisabled?: boolean;
};

/**
 * Individual past meeting item with expandable notes section.
 * Displays read-only notes from previous meetings.
 */
const PastMeetingItem = ({
	meetingId,
	meetingName,
	startDateTime,
	endDateTime,
	participants,
	workspaceParticipants,
	isExpanded,
	onToggle,
	userId,
}: PastMeetingItemProps): React.ReactElement => {
	const [selectedNoteType, setSelectedNoteType] = useState<NoteType>("my-shared");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const notesMeetingId = isExpanded ? meetingId : "";
	const { data: notes, isLoading: isNotesLoading } = useMeetingNotes(notesMeetingId);
	const notesData = usePastMeetingNotesData({ notes, userId, participants });

	const otherParticipant = participants?.find((p) => p.userId !== userId);
	const otherParticipantName =
		workspaceParticipants?.find((p) => p.userId === otherParticipant?.userId)?.name ||
		otherParticipant?.name ||
		"Participant";

	const getNoteTypeLabel = (type: NoteType): string => {
		switch (type) {
			case "my-shared":
				return "My Shared Notes";
			case "my-private":
				return "My Private Notes";
			case "other-shared":
				return `${otherParticipantName}'s Shared Notes`;
		}
	};

	const getCurrentNoteContent = (): string => {
		switch (selectedNoteType) {
			case "my-shared":
				return notesData.primaryPublicNotes?.content || "";
			case "my-private":
				return notesData.userPrivateNotes?.content || "";
			case "other-shared":
				return notesData.otherParticipantNotes?.content || "";
		}
	};

	const getAvatarId = (): string => {
		if (selectedNoteType === "other-shared") {
			return otherParticipant?.userId || "";
		}
		return userId;
	};

	const noteContent = getCurrentNoteContent();

	const noteTypeOptions: NoteType[] = otherParticipant
		? ["my-shared", "my-private", "other-shared"]
		: ["my-shared", "my-private"];

	useEffect(() => {
		if (!otherParticipant && selectedNoteType === "other-shared") {
			setSelectedNoteType("my-shared");
		}
	}, [otherParticipant, selectedNoteType]);

	const dropdownOptions = noteTypeOptions.map((type) => ({
		id: type,
		label: (
			<div className="flex items-center gap-2">
				<Avatar
					id={type === "other-shared" ? otherParticipant?.userId || "" : userId}
					size={config.layout.dropdownAvatarSize}
				/>
				<Text size="paragraph-3" weight="regular">
					{getNoteTypeLabel(type)}
				</Text>
			</div>
		),
	}));

	const handleDropdownSelect = (option: { id: string }): void => {
		setSelectedNoteType(option.id as NoteType);
	};

	const trigger = (
		<button className="flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0">
			<Avatar id={getAvatarId()} size={config.layout.dropdownAvatarSize} />
			<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="hover:cursor-pointer hover:underline">
				{getNoteTypeLabel(selectedNoteType)}
			</Text>
			<Icon icon={faCaretDown} size={config.iconSize.medium} color="neutral-5" />
		</button>
	);

	return (
		<div className="border-b border-solid border-neutral-2">
			<div
				className="flex h-12 cursor-pointer select-none items-center gap-2 bg-white px-4 py-2 hover:bg-neutral-1"
				onClick={onToggle}
			>
				<div className="flex min-w-0 flex-1 flex-col items-start justify-center">
					<div className="w-full py-line-clamp-1">
						<Text size="paragraph-3" weight="semi-bold">
							{meetingName}
						</Text>
					</div>
					<Text size="caption" weight="regular" color="neutral-9">
						{formatPastMeetingDateTime(startDateTime, endDateTime)}
					</Text>
				</div>
				<div className={isExpanded ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"}>
					<ButtonIcon icon={faAngleDown} onClick={onToggle} />
				</div>
			</div>

			{isExpanded && (
				<div className="flex flex-col gap-2 border-t border-solid border-neutral-2 bg-white p-4">
					{isNotesLoading ? (
						<div className="flex items-center justify-center py-4">
							<Spinner size={config.spinnerSize.small} />
						</div>
					) : (
						<div className="flex flex-col items-start gap-2">
							<div className="relative flex h-5 w-full items-center">
								<FloatingDropdown
									isOpen={isDropdownOpen}
									onOpenChange={setIsDropdownOpen}
									trigger={trigger}
									options={dropdownOptions}
									onSelect={handleDropdownSelect}
									placement="bottom-start"
									zIndex={config.zIndex.pastMeetingDropdown}
									className="min-w-[200px]"
								/>
							</div>

							<ExpandableText
								key={selectedNoteType}
								content={noteContent}
								collapseHeight={config.pastMeetingNotesCollapseHeight}
								emptyMessage="No notes recorded"
								className="w-full"
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default PastMeetingItem;
